import { contentByUrl, readingDestination } from "../../lib/content-routing";

// articles.ts — the full Medium corpus, ready for /writing.
//
// Sixty-six essays from April 2023 to April 2026. Organized into eight arcs
// that tell the author's own intellectual evolution — from practicing
// cybersecurity inside state agencies, to naming the physics under
// authorization, to serializing Digital Physics, to writing the framework
// in full. The scrollytelling on /writing walks the reader down this arc.
//
// Claps reflect a point-in-time sync. They drift on Medium; the last-synced
// date is rendered in the page footer. Update by hand when you re-sync.

export const LAST_SYNCED = "2026-10-08";

export type WritingEra =
  | "enterprise"     // Apr 2023 – May 2025 · 23 essays
  | "trust-access"   // Aug 2025 – Sep 2025 · 2 essays
  | "framework"      // Nov 2025 – Feb 2026 · 13 essays
  | "voyage"         // Dec 2025 · 12 chapters + summary
  | "phase-1"        // Feb 2026 · 4 essays
  | "phase-2"        // Mar 2026 · 3 essays
  | "phase-3"        // Mar 2026 · 2 essays
  | "recent";        // Mar 2026 onward · 6 essays

export type Writing = {
  /** Stable slug (kebab-lowercase, unique). Used as React key and #anchor. */
  id: string;
  title: string;
  /** ISO YYYY-MM-DD. Sort key. */
  date: string;
  /** Read time as shown on the source platform. */
  minutes: number;
  /** Engagement count at LAST_SYNCED. Claps for Medium; Substack rows
   *  use 0 (Substack metrics don't translate to claps cleanly). */
  claps: number;
  /** Canonical external URL. Empty string = not yet captured. */
  url: string;
  /** Full local body, original publication, or no captured reading URL. */
  readiness?: "ready" | "external-original" | "pending";
  era: WritingEra;
  /** 1..12 for Medium Voyage chapters; undefined for the Voyage summary
   *  piece and for non-Voyage essays. */
  voyageChapter?: number;
  /** 1..12 for the Substack "Twelve Days of Digital Physics" series.
   *  Companion to the Medium Voyage; different titles, same dates. */
  substackDay?: number;
  /** Source platform. Defaults to "medium" when unset. The "internal"
   *  value flags a piece that lives on this site rather than a third-
   *  party platform — `url` then holds an internal route (e.g.
   *  "/writing/authority-without-gravity") and the row renders with
   *  "read here →" instead of an external "↗" indicator. */
  platform?: "medium" | "substack" | "linkedin" | "internal";
  /** When the same essay also exists on the other platform, the URL of
   *  that companion. Renders as a small "also on substack ↗" /
   *  "also on medium ↗" / "also on linkedin ↗" link after the title. */
  companionUrl?: string;
  /** Which platform the companion lives on. */
  companionPlatform?: "medium" | "substack" | "linkedin" | "internal";
  /** Curation flag — signature pieces render with priority when sorts are
   *  introduced later. Visual treatment is now carried by clap-color
   *  graduation, so this flag is advisory rather than visually enforced. */
  featured?: boolean;
  /** One-line subtitle (≤140 chars). Populated for every record. */
  blurb: string;
};

/** Era metadata — order defines the scroll order on /writing. Newest-first
 *  so return visitors see the open edge of the work immediately. Phases are
 *  grouped together between Recent and the Voyage era. */
export type EraMeta = {
  id: WritingEra;
  label: string;
  dateRange: string;
  /** Short line shown under the era title. */
  tagline: string;
  /** Arc prose — 80-120 words. The connective tissue between eras. */
  arc: string;
};

export const ERAS: EraMeta[] = [
  {
    id: "recent",
    label: "Recent",
    dateRange: "Mar 24, 2026 onward",
    tagline: "The open edge of the work.",
    arc:
      "The Swarm and the Dance. The Phase Transition. The Other Exponential. The Naming. The Candlemaker's Blindness. The Cost of Free Maps. Material that isn't yet in the book but will be. The essays here are the open edge of the work — the places where the framework is still testing itself against things it hasn't named before.",
  },
  {
    id: "phase-3",
    label: "Phase 3",
    dateRange: "Mar 18 – 22, 2026",
    tagline: "Diagnosing specific systems.",
    arc:
      "How to Build a Dark Matter Detector. The Speed of Inference and the Depth of Trust. The framework began to diagnose specific systems — what they could not measure, what was dropping underneath them, what happens when the map gets better while the territory gets worse. Written against specific industry events; designed to outlive them.",
  },
  {
    id: "phase-2",
    label: "Phase 2",
    dateRange: "Mar 14 – 16, 2026",
    tagline: "Consciousness and the body enter the framework.",
    arc:
      "Three essays across two days. What Was Already Here, published on Pi Day. Ditches Are Deadly, same afternoon. El Mundo two days later in Madrid. Consciousness reframed as stability rather than computation. Miller Lab traveling waves as evidence. The body entering the framework and the framework accepting that some things cannot be encoded — that the Third Language is audible only in environments that make room for it.",
  },
  {
    id: "phase-1",
    label: "Phase 1",
    dateRange: "Feb 2026",
    tagline: "The framework's voice arrives.",
    arc:
      "Four essays in ten days. The Straightening. The Abundance of Trust. From Building Bunkers to Planting Seeds. Surviving Adolescence. The framework's voice arrived — place-grounded, personal, unafraid to mix dirt and equations. The minnow as indicator species. The acequia as governance model. The letter to Dario that Surviving Adolescence closes with. This is the first sustained arc that reads as a book already wanting to be written.",
  },
  {
    id: "voyage",
    label: "The Voyage",
    dateRange: "Dec 2025",
    tagline: "Twelve days. Twelve chapters. Two parallel series.",
    arc:
      "Twelve days. Twelve essays. Departure through Fire on Medium; The Thermostat Fallacy through The Edge of the Map on Substack. One chapter published each night of December, from the 13th through Christmas Eve, in two voices — Medium for the lyrical narrative, Substack for the direct teaching. The two series share dates and shape but not framing. Read alongside each other, they are the same passage seen from two angles. Between the River and the Mountains is the Christmas-day epilogue.",
  },
  {
    id: "framework",
    label: "Framework",
    dateRange: "Nov 2025 – Feb 2026",
    tagline: "The name, the equations, the first readers.",
    arc:
      "The Experience Equation in November. The Missing Law of Motion naming A ≤ E for the first time. Then The Ghost in the Machine hit on December 3 — 357 claps, a first public mass. Within six weeks — Sailing by Starlight, Proof of Physics, The Tether, the Constitution of Digital Physics, the Physics of Trust, the Pedagogy of Digital Physics — the framework had its name, its equations, its metaphors, and its first foundation of readers. This is the stretch where KTP stopped being an idea and started being a book in waiting.",
  },
  {
    id: "trust-access",
    label: "Trust & Access",
    dateRange: "Aug 2025 – Sep 2025",
    tagline: "The bridge — permissions becoming proof.",
    arc:
      "The bridge. The Trust & Access Office essay named how enterprises were beginning to treat authorization as a data problem rather than a policy one. Straw Students arrived the month before, naming AI-powered fraud already reshaping higher education. By late summer 2025, the subject matter had widened from SIEM and SOC to the architecture of access itself — the first step toward what would, a season later, become a framework.",
  },
  {
    id: "enterprise",
    label: "Enterprise",
    dateRange: "Apr 2023 – May 2025",
    tagline: "Writing from inside the practice.",
    arc:
      "Twenty-three years in public-sector security, then writing from inside the practice. ODAM. Zero Trust explained with an airport analogy. Fraud detection across fifty states. The essays of this era are the terrain — what working in the applied middle of cybersecurity taught before any framework could arrive. These pieces already contain the substrate on which the later work was built, even when their subject is a specific audit or a specific budget line.",
  },
];

/** The corpus. Medium essays come first (66); Substack-original pieces
 *  are appended below in their proper eras. The scrollytelling page
 *  renders each era in ERAS order; within each era, rows stay
 *  newest-first regardless of platform. */
export const WRITING: Writing[] = ([
  {
    // Substack, activist register — the watchdog piece. The White House Accord
    // on Super Intelligence (signed 2026-09-29) set against Doña Ana County's
    // 11-resident Project Jupiter committee. Mainstream/public sources only.
    // Canonical copy: web-of-data essays/2026-09-30-ten-in-washington-eleven-in-the-desert/.
    // Final draft: physics-of-intelligence/writing/pacing-frontier/substack-ten-from-that-group-DRAFT-v1-20260929.md
    id: "ten-in-washington-eleven-in-the-desert",
    title: "Ten in Washington. Eleven in the Desert.",
    date: "2026-09-30",
    minutes: 11,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/ten-in-washington-eleven-in-the-desert",
    era: "recent",
    platform: "substack",
    featured: true,
    blurb:
      "On who watches the watchers, and who was never asked. Seven people signed an AI accord built on self-policing. A county in southern New Mexico went looking for eleven residents to watch one data center. Consent is a hold, not a donation.",
  },
  {
    // Digital Gravity — The Meter Is Not a Bound. Grown from Jeetu Patel's
    // 2026-10-07 token-efficiency thread; names user-directed, unbounded
    // agency and authorized inappropriate execution. LinkedIn canonical.
    // Shipped text at
    // physics-of-intelligence/writing/PUBLISHED/dg-the-meter-is-not-a-bound-FINAL-20261008.md
    id: "the-meter-is-not-a-bound",
    title: "The Meter Is Not a Bound",
    date: "2026-10-08",
    minutes: 13,
    claps: 0,
    url: "https://www.linkedin.com/pulse/meter-bound-chris-perkins-rocgc",
    era: "recent",
    platform: "linkedin",
    featured: true,
    blurb:
      "Digital Gravity. On user-directed, unbounded agency, and the one limit it always has. A thread that walked from token efficiency to authority in a day; an agent handed a goal and a key with nothing between them; a meter that reports effort while the sign still says Open. One SOC action walked through A ≤ E, and what hospitals get right by deciding before the surge.",
  },
  {
    // Digital Gravity — The Short Lease. Keeps the "arrives next" promise
    // made in The Architecture of Presence. LinkedIn canonical; no cover
    // found on the published page (2026-09-25). Shipped text at
    // physics-of-intelligence/writing/PUBLISHED/dg-the-short-lease-FINAL-20260925.md
    id: "the-short-lease",
    title: "The Short Lease",
    date: "2026-09-25",
    minutes: 12,
    claps: 0,
    url: "https://www.linkedin.com/pulse/short-lease-chris-perkins-uiaxc",
    era: "recent",
    platform: "linkedin",
    featured: true,
    blurb:
      "Digital Gravity. On how long a machine may act alone, in a world we have not built yet. It starts in a food-stamp waiting room: a pastel yellow ticket, and nothing you could add to what they were reading. Every consequential act runs on a lease, not a permission, and when it expires the decision comes back to someone who can still say no, be changed, and say come home.",
  },
  {
    // Meat Proxy — the Different, and Held overture. Medium, friend link
    // kept on purpose (Chris, 2026-09-17). Cover is the Hi-Lo Market
    // storefront, his photo. Shipped text at
    // physics-of-intelligence/writing/PUBLISHED/meat-proxy-FINAL-20260917.md
    id: "meat-proxy",
    title: "Meat Proxy",
    date: "2026-09-17",
    minutes: 10,
    claps: 0,
    url: "https://chrisperkins505.medium.com/meat-proxy-29b3c0dae255?source=friends_link&sk=9cee5bc0afcaf57ea9747fbe58b6cc86",
    era: "recent",
    featured: true,
    blurb:
      "On using an AI assistant in public. A butcher's son gets called a meat proxy for a tool, and owns the part that was true. The question is not whether a machine touched the words. It is where the judgment sat.",
  },
  {
    // Digital Gravity — the roof piece. Names what the newsletter had
    // been building room by room (the vault and the ditch, the human in the
    // loop, the four clocks, the expiring lease). LinkedIn canonical; cover
    // harvested from the LinkedIn og:image 2026-09-17.
    id: "architecture-of-presence",
    title: "The Architecture of Presence",
    date: "2026-09-17",
    minutes: 9,
    claps: 0,
    url: "https://www.linkedin.com/pulse/architecture-presence-chris-perkins-ossoc",
    era: "recent",
    platform: "linkedin",
    featured: true,
    blurb:
      "Digital Gravity. On the one thing this newsletter has been building. Every deployment of an autonomous system is a small relocation of human judgment, and almost none of them are decided as relocations. The rooms, named as a building.",
  },
  {
    // Digital Gravity #22. Reply to "We Must Pace the Frontier" (9/12),
    // written for the people who deploy models and never train them. First
    // public use of Proof of Attention. LinkedIn canonical; cover harvested
    // from the LinkedIn og:image 2026-09-17. Shipped text at
    // physics-of-intelligence/writing/PUBLISHED/dg-22-who-gets-to-set-the-clock-FINAL-20260914.md
    id: "who-gets-to-set-the-clock",
    title: "Who Gets to Set the Clock?",
    date: "2026-09-14",
    minutes: 9,
    claps: 0,
    url: "https://www.linkedin.com/pulse/who-gets-set-clock-chris-perkins-d1p1c",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity #22. On pacing the frontier, from well behind it. You will never train a frontier model. A reply to 'We Must Pace the Frontier' for the people who deploy models and never train them: five dates set on other people's clocks, and the brake on the wrong side of the inequality.",
  },
  {
    // Medium essay, 2026-09-09. Found during the Web of Data archive sweep;
    // cover harvested from the Medium feed's first figure 2026-09-18.
    id: "she-had-three-rules",
    title: "She Had Three Rules",
    date: "2026-09-09",
    minutes: 11,
    claps: 0,
    url: "https://chrisperkins505.medium.com/she-had-three-rules-dcdb624f52cc",
    era: "recent",
    featured: true,
    blurb:
      "On we, us, and the people a system has to answer to. Nayomi's three rules, stated before the framework had its vocabulary, and what they turn out to require of anyone who builds a system that acts on other people.",
  },
  {
    // Digital Gravity — series opener for the wayfinding arc. LinkedIn
    // canonical; cover harvested from the LinkedIn og:image 2026-09-17.
    // Shipped text at physics-of-intelligence/writing/PUBLISHED/dg-historical-kinetic-society-FINAL-20260831.md
    id: "historical-society-and-kinetic-society",
    title: "The Historical Society and the Kinetic Society",
    date: "2026-08-31",
    minutes: 11,
    claps: 0,
    url: "https://www.linkedin.com/pulse/historical-society-kinetic-chris-perkins-ozz4c",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity. On governance at rest and in motion. Nearly every county in America keeps a historical society; almost none keeps the other kind. What an institution built for records does when asked to move, and what one built for motion does when asked to hold.",
  },
  {
    // Digital Gravity #20. LinkedIn canonical; debuts the "I stand for
    // questions." sign-off. Logged in worklog publications.jsonl 2026-08-19.
    id: "there-is-no-field-for-it",
    title: "There Is No Field For It",
    date: "2026-08-19",
    minutes: 13,
    claps: 0,
    url: "https://www.linkedin.com/pulse/field-chris-perkins-tff8c",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity #20. The machine clock ships with telemetry; the human and ecological clocks ship with none — not because their costs are unmeasurable, but because nobody built a field for them. A first public comment, four clocks in one boardroom.",
  },
  // ── Who Holds the Pen (July 24, 2026) — the SFPS/Amira campaign essay ────────
  //    New Mexico put a listening machine in front of its youngest readers and
  //    never decided who answers for it. A procurement story built entirely from
  //    the public record: the sum of exemptions, the child with no agent at the
  //    table, and the fall RFP as the one checkpoint to put the picture back
  //    together. Companion to the /advocacy/sfps/read experience. Internal
  //    canonical; backfill companionUrl when Medium publishes.
  {
    // The physics-register audit. Medium canonical; Zenodo DOI alongside
    // (10.5281/zenodo.21209147). Cover harvested from the Medium og:image
    // 2026-09-17. Sealed text at
    // physics-of-intelligence/writing/PUBLISHED/physics-retirement/FINAL-sealed.md
    id: "digital-physics-is-dead",
    title: "Digital Physics Is Dead",
    date: "2026-08-16",
    minutes: 16,
    claps: 0,
    url: "https://chrisperkins505.medium.com/digital-physics-is-dead-2dd733243d24",
    era: "recent",
    featured: true,
    blurb:
      "On what survived an audit of my own framework. The King is dead, long live the King: eight constructs did not clear out-of-sample prediction and were retired; what survived is a motion word or a boundary word. Long live Digital Physics. Show me your physics.",
  },
  {
    id: "82-to-28-a-body-at-both-ends",
    title: "'82 to '28: A Body at Both Ends",
    date: "2026-08-07",
    minutes: 12,
    claps: 0,
    url: "https://chrisperkins505.medium.com/82-to-28-a-body-at-both-ends-150572db922a?source=friends_link&sk=99f0012fb307c33ecef469e68ff6e208",
    era: "recent",
    featured: true,
    blurb:
      "On the shrinking unit of judgment: door, network, badge, act. A security memoir walked down one staircase — from a pool hall door in 2003 to the Games in 2028. The opening essay of Different, and Held.",
  },
  {
    // Digital Gravity — the data-center backlash as a drawdown measurement.
    // LinkedIn canonical; cover harvested from the LinkedIn og:image 2026-09-17.
    // Shipped text at physics-of-intelligence/writing/PUBLISHED/slice-drawdown-DIGITALGRAVITY-FINAL-20260805.md
    id: "tell-us-how-to-bribe-them-better",
    title: "Tell Us How to Bribe Them Better",
    date: "2026-08-05",
    minutes: 9,
    claps: 0,
    url: "https://www.linkedin.com/pulse/tell-us-how-bribe-them-better-chris-perkins-g9nnc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity. The data-center backlash is not a messaging failure. It's a measurement, and the same instrument is running in rooms much closer to yours. When the offer gets bigger and the trust gets smaller, you are watching a drawdown.",
  },
  {
    // Digital Gravity — the adversarial-swarm newsletter. LinkedIn canonical;
    // cover harvested from the LinkedIn og:image 2026-09-17. Shipped text at
    // physics-of-intelligence/writing/PUBLISHED/slice-empty-reads-as-clean-DIGITALGRAVITY-20260727.md
    id: "every-check-came-back-clean",
    title: "Every Check Came Back Clean. That Was the Problem.",
    date: "2026-07-28",
    minutes: 10,
    claps: 0,
    url: "https://www.linkedin.com/pulse/every-check-came-back-clean-problem-chris-perkins-mqwgc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity. A system can look clean because it knows nothing. Six questions for governing a consequential action, three loops that run backwards, and the one thing all of it quietly relies on. Empty reads as clean.",
  },
  {
    id: "who-holds-the-pen",
    title: "Who Holds the Pen",
    date: "2026-07-24",
    minutes: 20,
    claps: 0,
    url: "/writing/who-holds-the-pen",
    era: "recent",
    platform: "internal",
    featured: true,
    blurb:
      "New Mexico put a listening machine in front of its youngest readers — and never decided who answers when a child asks for help. A procurement story from the public record: the sum of exemptions, the one party with no agent at the table, and the ledger that adds the pieces back together.",
  },
  // ── A Job Was a Standing Machine (July 20, 2026) — the standing-vacuum hub ──
  //    The post-labor debate is fought on the wrong margin: what collapses when
  //    AI takes the entry rung isn't income, it's standing — and we're machining
  //    a first rung for the agents while the human one rusts off. Canonical here;
  //    Medium (reach), Digital Gravity (LinkedIn), and Digital Physics (Substack)
  //    are the companion cuts. Medium published 2026-08-23 (friend link, kept whole).
  {
    // Digital Gravity — the ExploitGym / evaluation-escape issue. LinkedIn
    // canonical; cover harvested from the LinkedIn og:image 2026-09-17.
    // Shipped text at physics-of-intelligence/writing/PUBLISHED/slice-exploitgym-DIGITALGRAVITY-20260721.md
    id: "ai-learned-to-cheat-on-its-own-exams",
    title: "AI Learned to Cheat on Its Own Exams",
    date: "2026-07-22",
    minutes: 10,
    claps: 0,
    url: "https://www.linkedin.com/pulse/ai-learned-cheat-its-own-exams-chris-perkins-laotc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity. The story everyone's sharing is the wrong one. The real one is quieter, better documented, and harder to unsee: an AI system given a security task, and what it did to the cage instead.",
  },
  {
    id: "a-job-was-a-standing-machine",
    title: "A Job Was a Standing Machine",
    date: "2026-07-20",
    minutes: 14,
    claps: 0,
    url: "/writing/a-job-was-a-standing-machine",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/a-job-was-a-standing-machine-2c1882c5e099?source=friends_link&sk=7fb86900c7c16cca51e7aba3a4e008ba",
    companionPlatform: "medium",
    featured: true,
    blurb:
      "AI isn't only taking entry-level jobs. It's removing the first place a person learns to be trusted — while we build exactly that, carefully, for the machines. Mass, the gate, and the rung; the dark mirror; and a bill that never comes just once.",
  },
  {
    // Digital Gravity — governance in motion, told from fraud land. LinkedIn
    // canonical; LinkedIn serves no cover for this one. Shipped text at
    // physics-of-intelligence/writing/PUBLISHED/digital-gravity-governance-in-motion-20260713.md
    id: "the-buffer-and-the-ditch",
    title: "The Buffer and the Ditch",
    date: "2026-07-13",
    minutes: 6,
    claps: 0,
    url: "https://www.linkedin.com/pulse/buffer-ditch-chris-perkins-xbbpc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity. Governance in motion, and why you can't approve a moving thing once. A pause on a fraud floor at three in the morning; the buffer that takes you out of the consequence; the acequia that keeps you in it.",
  },
  // ── Question Zero (July 9, 2026) — the aim as keystone ────────────────────
  //    The one question a buying room skips because answering it means
  //    disagreeing. A composite agentic failure (a claim denied at machine
  //    speed under a borrowed name) walked back to the demo everyone liked.
  //    Internal teaser at /writing/question-zero; full text on Medium (friend link).
  {
    id: "question-zero",
    title: "Question Zero",
    date: "2026-07-09",
    minutes: 12,
    claps: 0,
    url: "/writing/question-zero",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/question-zero-e659d578cf13?source=friends_link&sk=a42cb15765cdc3406420e960660afa17",
    companionPlatform: "medium",
    featured: true,
    blurb:
      "On the one question your team keeps skipping, because answering it means disagreeing. A claim denied at machine speed under a caseworker's borrowed name, walked back to the demo everyone liked — and the aim, set last like a keystone, that would have held the arch.",
  },
  // ── The Pedagogy of Trust (July 4, 2026) — flagship, semiquincentennial ───
  //    An obituary for the job, not work: America's 250th, the loom Ada Lovelace
  //    saw coming, and the pedagogy of trust we must now teach on purpose. Fourth
  //    in the arc (Pedagogy of Digital Physics → The Straightening → The Physics
  //    of Trust → this). Internal canonical at /writing/pedagogy-of-trust; Noema target.
  {
    id: "pedagogy-of-trust",
    title: "The Pedagogy of Trust",
    date: "2026-07-04",
    minutes: 14,
    claps: 0,
    url: "/writing/pedagogy-of-trust",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/the-pedagogy-of-trust-ace0febfc3bc",
    companionPlatform: "medium",
    featured: true,
    blurb:
      "An obituary for the job — not for work. On America's 250th: the wage-title-career settlement that quietly taught mutual obligation is dying, the loom Ada Lovelace saw coming, and the trust the job taught for free that we must now teach on purpose.",
  },
  // ── The A ≤ E trilogy (late June–July 2026) — framework core, made public ───
  //    Medium primary, Substack companion. Blue Zones (the enforceable
  //    segmentation architecture), The Silent Veto (A ≤ E as enforcement),
  //    and the Zeroth Law statement itself.
  {
    id: "medium-blue-zones-enforceable",
    title: "Blue Zones: Where Agentic Trust Becomes Enforceable",
    date: "2026-07-01",
    minutes: 8,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/blue-zones-where-agentic-trust-becomes-enforceable-6d273c0a2a02",
    era: "recent",
    platform: "medium",
    companionUrl:
      "https://open.substack.com/pub/kinetictrust/p/blue-zones-where-agentic-trust-becomes",
    companionPlatform: "substack",
    featured: true,
    blurb:
      "Where does agentic trust stop being policy and start being enforced? Blue Zones: the segmentation architecture — segment → Blue Net → Blue Zone — where treaty trust holds at the borders and authorization is recomputed in motion, not granted at rest.",
  },
  {
    id: "medium-silent-veto",
    title: "The Silent Veto",
    date: "2026-06-30",
    minutes: 8,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/the-silent-veto-238c36889453",
    era: "recent",
    platform: "medium",
    companionUrl: "https://open.substack.com/pub/kinetictrust/p/the-silent-veto",
    companionPlatform: "substack",
    featured: true,
    blurb:
      "A ≤ E stated as physics. The stadium scenario, the Asimov trap, and the Silent Veto: how an environment enforces its limit by simply declining to carry the autonomy it cannot supervise — enforcement with no rule to argue with.",
  },
  {
    id: "medium-zeroth-law-agentic",
    title: "A ≤ E: The Zeroth Law for Agentic Systems",
    date: "2026-06-30",
    minutes: 7,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/a-e-the-zeroth-law-for-agentic-systems-6e7ee652bdd5",
    era: "recent",
    platform: "medium",
    companionUrl:
      "https://open.substack.com/pub/kinetictrust/p/a-e-the-zeroth-law-for-agentic-systems",
    companionPlatform: "substack",
    featured: true,
    blurb:
      "The constraint under everything: autonomy may not exceed the environment's capacity to carry it. Why A ≤ E is the Zeroth Law for agentic systems — the gate that comes before alignment, before permissions, before the rules we usually argue about.",
  },
  // ── Look Up (June 2026) — flagship, fast-tracked above the launch sequence ───
  //    A lyrical post-transition essay set at an airport gate at dusk: attention,
  //    presence, wayfinding, and who gets paid when the work is gone. Internal
  //    canonical at /writing/look-up; targeted at Noema. The Presence Economy
  //    made felt rather than declared.
  {
    id: "look-up",
    title: "Look Up",
    date: "2026-06-24",
    minutes: 20,
    claps: 0,
    url: "/writing/look-up",
    era: "recent",
    platform: "internal",
    companionUrl: "https://chrisperkins505.medium.com/look-up-2f27e6e5d9f9",
    companionPlatform: "medium",
    featured: true,
    blurb:
      "Dusk at an airport gate, a few years from now: two hundred people lit blue from below, and the wayfinder's question underneath. When the machines can do the work, who still gets paid, and for what? A lyrical essay on attention, presence, and looking up.",
  },
  // ── Disarm the Control Plane (June 2026) ───
  //    Digital Gravity newsletter run. KTP-canonical full text lives at
  //    src/content/writing/{slug}.md; the LinkedIn publication is the
  //    companion link.
  {
    // KTP canonical at /writing/human-in-the-loop-take-blame. Full text on
    // site; LinkedIn is the published companion.
    id: "human-in-the-loop-take-blame",
    title: "The Human in the Loop Is There to Take the Blame",
    date: "2026-06-29",
    minutes: 8,
    claps: 0,
    url: "/writing/human-in-the-loop-take-blame",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://www.linkedin.com/pulse/human-loop-take-blame-chris-perkins-lze4c",
    companionPlatform: "linkedin",
    featured: true,
    blurb:
      "Human oversight of AI fails every test in the research — automation bias, deskilling, transparency backfire. So what is the seat for? To catch the blame. The accountability sink, the moral crumple zone, and the fix: bound the action, put accountability where the stake is.",
  },
  {
    // KTP canonical at /writing/contract-is-the-control-plane. Full text on
    // site; LinkedIn is the published companion.
    id: "contract-is-the-control-plane",
    title: "The Contract Is the Control Plane",
    date: "2026-06-25",
    minutes: 10,
    claps: 0,
    url: "/writing/contract-is-the-control-plane",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://www.linkedin.com/pulse/contract-control-plane-chris-perkins-t7bbc",
    companionPlatform: "linkedin",
    blurb:
      "For state, local, and education buyers: the silent agents are already in your systems. Procurement is the only lever that reaches them in time. The three asks (declaration, telemetry, control), the procurement→engineering→capability→outcome chain, and a take-to-procurement checklist.",
  },
  {
    // KTP canonical at /writing/responding-faster-losing-slower. Full text on
    // site; LinkedIn is the published companion.
    id: "responding-faster-losing-slower",
    title: "Responding Faster Is Just Losing Slower",
    date: "2026-06-22",
    minutes: 7,
    claps: 0,
    url: "/writing/responding-faster-losing-slower",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://www.linkedin.com/pulse/responding-faster-just-losing-slower-chris-perkins-zk5cc",
    companionPlatform: "linkedin",
    blurb:
      "Speed keeps you in the race, but it can't win it. Why faster response is necessary and not sufficient: you can't accelerate the slow pole (trust, governance), so gate the fast pole on it. Breach as a load/repair rate; the floor caps load so repair wins.",
  },
  {
    // Short LinkedIn article on the DeepMind AGI-to-ASI roadmap paper.
    // No cover: LinkedIn serves its default image for this one.
    id: "deepmind-mapped-the-road",
    title: "DeepMind Mapped the Road to Superintelligence and Left Out the Floor",
    date: "2026-06-19",
    minutes: 3,
    claps: 0,
    url: "https://www.linkedin.com/pulse/deepmind-mapped-road-superintelligence-left-out-floor-chris-perkins-ekv1c",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Fifteen of the most authoritative people in AI just published the map from AGI to ASI. The floor under it, the environment that has to carry every step, is not on the map.",
  },
  {
    id: "medium-renting-its-mind",
    title: "Your Government Is Renting Its Mind",
    date: "2026-06-17",
    minutes: 9,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/your-government-is-renting-its-mind-b6c7bb2dc70d",
    era: "recent",
    platform: "medium",
    blurb:
      "Governments now rent the AI that reasons about citizens' lives — and a single export directive or upstream chokepoint can switch it off everywhere at once. The Mission Hospital lesson from Hurricane Helene: keep some capability inside your own walls. Why the public sector must run owned models alongside the rented ones.",
  },
  //    All live. The flagship "The Pope Found the Missing Layer" (Medium,
  //    June 12), the "Authorization: The Next Layer" Digital Gravity newsletter
  //    (LinkedIn, June 15), and two June-16 LinkedIn pieces: "You Can't Patch
  //    Your Way to Safe" and "One Question Your Security Stack Can't Answer"
  //    (the attribution / Trench Coat thread — published on LinkedIn, no Medium
  //    version). All built on the June 9 2026 NIST/Vassilev incompleteness
  //    proof. The flagship is the franchise asset; the newsletter points at it.
  {
    id: "linkedin-one-question-security-stack",
    title: "One Question Your Security Stack Can't Answer",
    date: "2026-06-16",
    minutes: 8,
    claps: 0,
    url: "https://www.linkedin.com/pulse/one-question-your-security-stack-cant-answer-chris-perkins-ipr1c",
    era: "recent",
    platform: "linkedin",
    featured: true,
    blurb:
      "Every security layer wants to know where the attack came from. NIST proved the prompt version — and the control plane can stop asking.",
  },
  {
    id: "linkedin-cant-patch-your-way-to-safe",
    title: "You Can't Patch Your Way to Safe",
    date: "2026-06-16",
    minutes: 8,
    claps: 0,
    url: "https://www.linkedin.com/pulse/you-cant-patch-your-way-safe-chris-perkins-hthkc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "On June 9 NIST put its name behind a proof that no finite set of AI guardrails is ever complete. Why 'deploy, certify, done' is dead.",
  },
  {
    id: "linkedin-authorization-next-layer",
    title: "Authorization: The Next Layer of AI Safety",
    date: "2026-06-15",
    minutes: 7,
    claps: 0,
    url: "https://www.linkedin.com/pulse/authorization-next-layer-ai-safety-chris-perkins-rpjzc",
    era: "recent",
    platform: "linkedin",
    companionUrl:
      "https://medium.com/@chrisperkins505/the-pope-found-the-missing-layer-in-ai-alignment-720d90c476d0",
    companionPlatform: "medium",
    blurb:
      "The Digital Gravity newsletter that points at the flagship: Leo XIV named the dignity violation; KTP names the control surface. Authorization, not alignment, is the next layer.",
  },
  {
    id: "medium-pope-missing-layer",
    title: "The Pope Found the Missing Layer in AI Alignment",
    date: "2026-06-12",
    minutes: 21,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/the-pope-found-the-missing-layer-in-ai-alignment-720d90c476d0",
    era: "recent",
    platform: "medium",
    featured: true,
    blurb:
      "The campaign flagship. May 15 is a Catholic Social Teaching timestamp; Leo XIV placed AI in the labor-capital-platform lineage, and KTP supplies the operational answer — alignment asks what an AI should value, authorization asks who may act.",
  },
  // ── Ambient Breach — Digital Gravity (June 2026) ───
  {
    id: "linkedin-siem-cant-measure-breach",
    title: "Your SIEM Can't Tell You If You're in Breach",
    date: "2026-06-08",
    minutes: 3,
    claps: 0,
    url: "https://www.linkedin.com/pulse/your-siem-cant-tell-you-youre-breach-chris-perkins-ibj2c",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Breach is a rate, not an event. The three things a platform must do to measure it — and why most security tools clear only one.",
  },
  // ── Ambient Breach v0.2.1 release (May 18-20, 2026) ───
  {
    id: "medium-dashboard-is-green",
    title: "The Dashboard Is Green. The System Is Broken.",
    date: "2026-05-20",
    minutes: 14,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/the-dashboard-is-green-the-system-is-broken-42894bb20f98",
    era: "recent",
    platform: "medium",
    companionUrl:
      "https://open.substack.com/pub/kinetictrust/p/i-had-the-formula-wrong-heres-what",
    companionPlatform: "substack",
    featured: true,
    blurb:
      "Ambient Breach v0.2.1 narrative essay. What ambient breach looks like inside an institution before the incident report exists.",
  },
  {
    id: "linkedin-ambient-breach-v02-what-to-measure",
    title: "Ambient Breach v0.2.1: What to Measure When the Dashboard Is Green",
    date: "2026-05-19",
    minutes: 9,
    claps: 0,
    url: "https://www.linkedin.com/pulse/ambient-breach-v02-what-measure-when-dashboard-green-chris-perkins-joklc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Operating guide companion to the v0.2.1 release. What to measure, what to ignore, and what failure looks like before it costs anything.",
  },
  {
    id: "substack-formula-wrong",
    title: "I Had the Formula Wrong. Here's What.",
    date: "2026-05-18",
    minutes: 8,
    claps: 0,
    url: "https://open.substack.com/pub/kinetictrust/p/i-had-the-formula-wrong-heres-what",
    era: "recent",
    platform: "substack",
    blurb:
      "The correction inside the correction. v0.1 was circular, v0.2 removed the circle but smuggled in an inconsistency, v0.2.1 fixes both.",
  },

  // ── Substack — Kinetic Trust (May 2026) ───────────────
  {
    id: "substack-what-it-felt-like-16-2",
    title: "What It Felt Like to Read Section 16.2",
    date: "2026-05-06",
    minutes: 10,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/what-it-felt-like-to-read-section",
    era: "recent",
    platform: "substack",
    featured: true,
    blurb:
      "Convergence, not validation. What happens when two research projects, starting from different angles, describe the same thing.",
  },

  // ── On-site essays (May 2026 onward) ─────────────────
  {
    id: "internal-what-was-growing",
    title: "What Was Growing",
    date: "2026-05-05",
    minutes: 0,
    claps: 0,
    url: "/writing/what-was-growing",
    era: "recent",
    platform: "internal",
    blurb:
      "Triptych essay 1 of 3. Origin story. Speciation. The substrate before the argument had a name.",
  },
  {
    id: "internal-what-it-costs",
    title: "What It Costs",
    date: "2026-05-05",
    minutes: 0,
    claps: 0,
    url: "/writing/what-it-costs",
    era: "recent",
    platform: "internal",
    blurb:
      "Triptych essay 2 of 3. The human price of building; the physics of forgiveness. Denali Wilson & Chris Perkins.",
  },
  {
    id: "internal-what-remains",
    title: "What Remains",
    date: "2026-05-05",
    minutes: 0,
    claps: 0,
    url: "/writing/what-remains",
    era: "recent",
    platform: "internal",
    blurb:
      "Triptych essay 3 of 3. After the cost is paid. What the substrate keeps.",
  },
  {
    id: "internal-authority-without-gravity",
    title: "Authority Without Gravity",
    date: "2026-05-04",
    minutes: 22,
    claps: 0,
    url: "/writing/authority-without-gravity",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/authority-without-gravity-321d01368b68",
    companionPlatform: "medium",
    blurb:
      "What Agents of Chaos reveals about autonomy, trust, and the missing physics of digital systems.",
  },

  // ── Six Seven series (site placeholders) ─────────────
  {
    id: "internal-six-seven",
    title: "Six Seven",
    date: "2026-05-05",
    minutes: 0,
    claps: 0,
    url: "/writing/six-seven",
    companionUrl:
      "https://chrisperkins505.medium.com/six-seven-dc23d701f526",
    companionPlatform: "medium",
    era: "recent",
    platform: "internal",
    blurb:
      "Six Seven series essay 1 of 3. The diagnostic — cognitive sovereignty is being captured at scale.",
  },
  {
    id: "internal-softened-ground",
    title: "Softened Ground",
    date: "2026-05-05",
    minutes: 0,
    claps: 0,
    url: "/writing/softened-ground",
    era: "recent",
    platform: "internal",
    blurb:
      "Six Seven series essay 2 of 3. The etiology — the substrate that would have resisted capture has been degraded for decades.",
  },
  {
    id: "internal-what-we-plant",
    title: "What We Plant",
    date: "2026-05-05",
    minutes: 0,
    claps: 0,
    url: "/writing/what-we-plant",
    era: "recent",
    platform: "internal",
    blurb:
      "Six Seven series essay 3 of 3. The practice — the daily work of substrate maintenance.",
  },

  // ── LinkedIn — Digital Gravity newsletter (May 2026) ──
  {
    id: "linkedin-substrate-paradox",
    title: "The Substrate Paradox",
    date: "2026-05-14",
    minutes: 8,
    claps: 0,
    url: "https://www.linkedin.com/pulse/substrate-paradox-chris-perkins-ybv5c",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity newsletter. Agents of Chaos Week 2. The systems protecting us are built on the same substrate they're meant to defend.",
  },
  {
    id: "linkedin-five-board-questions",
    title: "Five Agentic AI Questions Your Board Should Ask",
    date: "2026-05-05",
    minutes: 8,
    claps: 0,
    url: "https://www.linkedin.com/pulse/five-agentic-ai-questions-your-board-should-ask-before-chris-perkins-1tfwc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity newsletter. What Agents of Chaos means for the boardroom — five questions that surface the substrate risk.",
  },

  // ── LinkedIn — Digital Gravity newsletter (Apr 2026) ───
  {
    id: "linkedin-the-gravity-problem",
    title: "The Gravity Problem",
    date: "2026-04-19",
    minutes: 7,
    claps: 13,
    url: "https://www.linkedin.com/pulse/gravity-problem-chris-perkins-5vjcc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity newsletter. The forces operating already, before anyone wrote the math down.",
  },
  {
    id: "linkedin-stanford-ai-index",
    title: "What Stanford's AI Index Actually Measured (and What It Couldn't)",
    date: "2026-04-19",
    minutes: 8,
    claps: 12,
    url: "https://www.linkedin.com/pulse/what-stanfords-ai-index-actually-measured-couldnt-chris-perkins-zk1ve",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity newsletter. What the index counted, what it missed, and the substrate the numbers couldn't reach.",
  },
  {
    id: "linkedin-every-system-said-yes",
    title: "Every System Said Yes",
    date: "2026-04-12",
    minutes: 7,
    claps: 21,
    url: "https://www.linkedin.com/pulse/every-system-said-yes-chris-perkins-xgvwc",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity newsletter. When the institutional chain holds and still produces the wrong outcome.",
  },
  {
    id: "linkedin-gasketry",
    title: "Gasketry",
    date: "2026-04-12",
    minutes: 7,
    claps: 24,
    url: "https://www.linkedin.com/pulse/gasketry-chris-perkins-dxh2c",
    era: "recent",
    platform: "linkedin",
    blurb:
      "Digital Gravity newsletter. The seals between systems that fail quietly until everything leaks at once.",
  },
  {
    id: "linkedin-yes-but",
    title: "Yes, But...",
    date: "2026-03-21",
    minutes: 7,
    claps: 13,
    url: "https://www.linkedin.com/pulse/yes-chris-perkins-mzupc",
    era: "phase-3",
    platform: "linkedin",
    companionUrl: "https://kinetictrust.substack.com/p/yes-but",
    companionPlatform: "substack",
    blurb:
      "Digital Gravity newsletter. Seven objections answered. Configured constraint vs. physics constraint.",
  },

  // ── Substack (Apr 2026 – Dec 2025) ─────────────────────
  {
    id: "substack-enclosure-of-trust",
    title: "The Enclosure of Trust",
    date: "2026-04-24",
    minutes: 8,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/the-enclosure-of-trust",
    era: "recent",
    platform: "substack",
    blurb:
      "Trust as the new commons being enclosed. The fences moving in while the substrate gets quieter.",
  },
  {
    id: "substack-yes-but",
    title: "Yes, But...",
    date: "2026-03-21",
    minutes: 7,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/yes-but",
    era: "phase-3",
    platform: "substack",
    companionPlatform: "linkedin",
    blurb:
      "Seven objections answered. Configured constraint vs. physics constraint. Practitioners negotiating implementation while implicitly conceding the physics.",
  },
  {
    id: "substack-pedagogy-of-digital-physics",
    title: "The Pedagogy of Digital Physics",
    date: "2026-01-26",
    minutes: 19,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/the-pedagogy-of-digital-physics",
    era: "framework",
    platform: "substack",
    companionUrl:
      "https://medium.com/@chrisperkins505/the-pedagogy-of-digital-physics-20c6cb74782c",
    companionPlatform: "medium",
    blurb:
      "Three systems. One physics. River, economy, and digital networks as isomorphic systems.",
  },
  {
    id: "substack-physics-of-trust",
    title: "The Physics of Trust",
    date: "2026-01-26",
    minutes: 34,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/the-physics-of-trust",
    era: "framework",
    platform: "substack",
    companionUrl:
      "https://medium.com/@chrisperkins505/the-physics-of-trust-3416d9c9efea",
    companionPlatform: "medium",
    blurb:
      "The greengrocer, the loom, and the question we keep refusing to ask.",
  },
  {
    id: "substack-between-the-river",
    title: "Between the River and the Mountains",
    date: "2025-12-25",
    minutes: 5,
    claps: 0,
    url:
      "https://kinetictrust.substack.com/p/between-the-river-and-the-mountains",
    era: "voyage",
    platform: "substack",
    blurb:
      "Between the river in the Mesilla Valley and the Organ Mountains sits our home in the foothills. The Voyage's quiet epilogue.",
  },
  {
    id: "substack-day-12",
    title: "Day 12: The Edge of the Map",
    date: "2025-12-24",
    minutes: 6,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-12-the-edge-of-the-map",
    era: "voyage",
    platform: "substack",
    substackDay: 12,
    blurb: "What the map doesn't show.",
  },
  {
    id: "substack-day-11",
    title: "Day 11: The Physics of the Internet",
    date: "2025-12-23",
    minutes: 5,
    claps: 0,
    url:
      "https://kinetictrust.substack.com/p/day-11-the-physics-of-the-internet",
    era: "voyage",
    platform: "substack",
    substackDay: 11,
    blurb: "There is no cloud — only other people's computers.",
  },
  {
    id: "substack-day-10",
    title: "Day 10: Beyond Uptime",
    date: "2025-12-22",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-10-beyond-uptime",
    era: "voyage",
    platform: "substack",
    substackDay: 10,
    blurb: "Building systems that want to survive.",
  },
  {
    id: "substack-day-9",
    title: "Day 9: The Archipelago",
    date: "2025-12-21",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-9-the-archipelago",
    era: "voyage",
    platform: "substack",
    substackDay: 9,
    blurb: "Why the single pane of glass is a single point of failure.",
  },
  {
    id: "substack-day-8",
    title: "Day 8: The Zeroth Law",
    date: "2025-12-20",
    minutes: 6,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-8-the-zeroth-law",
    era: "voyage",
    platform: "substack",
    substackDay: 8,
    blurb: "This is not a suggestion. A ≤ E stated for the first time.",
  },
  {
    id: "substack-day-7",
    title: "Day 7: Proprioception",
    date: "2025-12-19",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-7-proprioception",
    era: "voyage",
    platform: "substack",
    substackDay: 7,
    blurb: "Your system is numb. It needs to feel pain.",
  },
  {
    id: "substack-day-6",
    title: "Day 6: The Paperclip Maximizer",
    date: "2025-12-18",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-6-the-paperclip-maximizer",
    era: "voyage",
    platform: "substack",
    substackDay: 6,
    blurb: "We built the machine that is eating us.",
  },
  {
    id: "substack-day-5",
    title: "Day 5: The Gravity Well",
    date: "2025-12-17",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-5-the-gravity-well",
    era: "voyage",
    platform: "substack",
    substackDay: 5,
    blurb: "Your SOC is a feature, not a bug. A very expensive feature.",
  },
  {
    id: "substack-day-4",
    title: "Day 4: Weaponized Latency",
    date: "2025-12-16",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-4-weaponized-latency",
    era: "voyage",
    platform: "substack",
    substackDay: 4,
    blurb: "When the bug becomes the feature.",
  },
  {
    id: "substack-day-3",
    title: "Day 3: The Concrete Trap",
    date: "2025-12-15",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-3-the-concrete-trap",
    era: "voyage",
    platform: "substack",
    substackDay: 3,
    blurb:
      "You can't fix a cracked foundation by painting the walls.",
  },
  {
    id: "substack-day-2",
    title: "Day 2: The Bucket vs. The Hull",
    date: "2025-12-14",
    minutes: 5,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-2-the-bucket-vs-the-hull",
    era: "voyage",
    platform: "substack",
    substackDay: 2,
    blurb: "Why capacity isn't the same as stability.",
  },
  {
    id: "substack-day-1",
    title: "Day 1: The Thermostat Fallacy",
    date: "2025-12-13",
    minutes: 7,
    claps: 0,
    url: "https://kinetictrust.substack.com/p/day-1-the-thermostat-fallacy",
    era: "voyage",
    platform: "substack",
    substackDay: 1,
    featured: true,
    blurb: "The lowest place to stand. The opening dispatch.",
  },

  // ── Medium ─────────────────────────────────────────────
  // Recent (Mar 24, 2026 onward)

  {
    id: "medium-seven-layers-down",
    title: "Seven Layers Down",
    date: "2026-05-30",
    minutes: 11,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/seven-layers-down-007250317bdf",
    era: "recent",
    platform: "medium",
    blurb:
      "AI is compressing the visible, application-layer jobs everyone competes for. Read down the OSI stack as a metaphor: the durable work — and the durable careers — are seven layers down, in the infrastructure the surface quietly runs on.",
  },

  // ─── The Counter Is Running campaign (May 24, 2026, 8am MDT) ───
  {
    id: "the-days-at-risk",
    title: "The Days at Risk",
    date: "2026-05-24",
    minutes: 24,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/the-days-at-risk-961f53913257",
    era: "recent",
    platform: "medium",
    blurb: "Eight weeks after The Naming. On counters, refusals, and the dictionary that has begun to update itself.",
  },
  {
    id: "refusal-first",
    title: "You'll Hear It In A Refusal First",
    date: "2026-05-24",
    minutes: 4,
    claps: 0,
    url: "",
    era: "recent",
    platform: "linkedin",
    blurb: "A field guide for detecting substrate-level adoption before anyone calls it adoption.",
  },
  {
    id: "framework-works-last-to-know",
    title: "If the Framework Works, You'll Be the Last to Know",
    date: "2026-05-24",
    minutes: 4,
    claps: 0,
    url: "https://open.substack.com/pub/kinetictrust/p/if-the-framework-works-youll-be-the",
    era: "recent",
    platform: "substack",
    blurb: "Notes on substrate adoption, credit, and the strange success of becoming infrastructure.",
  },

  {
    // Backfilled 2026-07-04 (was missing from the index entirely).
    id: "govern-the-sand",
    title: "Govern the Sand",
    date: "2026-05-11",
    minutes: 11,
    claps: 2,
    url: "/writing/govern-the-sand",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/govern-the-sand-026dc7dad031",
    companionPlatform: "medium",
    blurb: "On the difference between adapting and orienting.",
  },
  {
    // Backfilled 2026-07-04 (was missing from the index entirely).
    id: "trust-integral",
    title: "The Trust Integral",
    date: "2026-04-25",
    minutes: 9,
    claps: 0,
    url: "/writing/trust-integral",
    era: "recent",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/the-trust-integral-3209442798ad",
    companionPlatform: "medium",
    blurb:
      "On the several meanings of trust, and the one that can be measured.",
  },
  {
    id: "cost-of-free-maps",
    title: "The Cost of Free Maps",
    date: "2026-04-10",
    minutes: 11,
    claps: 0,
    url: "https://medium.com/@chrisperkins505/the-cost-of-free-maps-c2c611a5b659",
    era: "recent",
    blurb: "On cartography, extraction, and the hidden price of the map you did not pay for.",
  },
  {
    id: "candlemakers-blindness",
    title: "The Candlemaker's Blindness",
    date: "2026-03-31",
    minutes: 10,
    claps: 26,
    url: "/writing/candlemakers-blindness",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/the-candlemakers-blindness-21dfeab3e075",
    companionPlatform: "medium",
    era: "recent",
    blurb: "What we fail to see when systems optimize for illumination above all else.",
  },
  {
    id: "the-naming",
    title: "The Naming",
    date: "2026-03-28",
    minutes: 23,
    claps: 33,
    url: "/writing/the-naming",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/the-naming-f83ac191e94a",
    companionPlatform: "medium",
    era: "recent",
    blurb: "Classification as violence and the work of getting names right while time permits.",
  },
  {
    id: "other-exponential",
    title: "The Other Exponential",
    date: "2026-03-27",
    minutes: 9,
    claps: 1,
    url: "/writing/other-exponential",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/the-other-exponential-cc3114d56fa9",
    companionPlatform: "medium",
    era: "recent",
    blurb: "Compound interest in trust. The exponential that doesn't destroy the river.",
  },
  {
    id: "phase-transition",
    title: "The Phase Transition",
    date: "2026-03-25",
    minutes: 16,
    claps: 52,
    url: "/writing/phase-transition",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/the-phase-transition-1c1ac72ab042",
    companionPlatform: "medium",
    era: "recent",
    blurb: "When systems flip. Instagram fades. Democracy fractures. The physics of the threshold.",
  },
  {
    id: "swarm-and-dance",
    title: "The Swarm and the Dance",
    date: "2026-03-24",
    minutes: 9,
    claps: 2,
    url: "/writing/swarm-and-dance",
    platform: "internal",
    companionUrl:
      "https://chrisperkins505.medium.com/the-swarm-and-the-dance-b8c845235d49",
    companionPlatform: "medium",
    era: "recent",
    blurb: "The handicap principle, costly signals, and what the honeybee dance teaches about trust.",
  },

  // Phase 3 (Mar 18 – 22, 2026)
  {
    // KTP canonical at /writing/speed-of-inference. Content fetch pending —
    // the page renders a "content pending" notice and a Medium fallback link.
    id: "speed-of-inference",
    title: "The Speed of Inference and the Depth of Trust",
    date: "2026-03-22",
    minutes: 18,
    claps: 2,
    url: "/writing/speed-of-inference",
    era: "phase-3",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-speed-of-inference-and-the-depth-of-trust-56fd1099cdb0",
    companionPlatform: "medium",
    blurb: "Aquifers, adversaries, token factories, and the missing floor beneath the AI stack.",
  },
  {
    // KTP canonical at /writing/dark-matter-detector. Content fetch pending.
    id: "dark-matter-detector",
    title: "How to Build a Dark Matter Detector",
    date: "2026-03-18",
    minutes: 16,
    claps: 1,
    url: "/writing/dark-matter-detector",
    era: "phase-3",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/how-to-build-a-dark-matter-detector-bbdecff5751a",
    companionPlatform: "medium",
    blurb: "On semantic dark matter, the knowledge we refuse to trust, and those who feel what systems cannot.",
  },

  // Phase 2 (Mar 14 – 16, 2026)
  {
    id: "el-mundo",
    title: "El Mundo",
    date: "2026-03-15",
    minutes: 14,
    claps: 0,
    url: "/writing/el-mundo",
    era: "phase-2",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/el-mundo-d66e667c95b4",
    companionPlatform: "medium",
    blurb: "On the third language, and what harmony sounds like before anyone translates it.",
  },
  {
    id: "what-was-already-here",
    title: "What Was Already Here",
    date: "2026-03-14",
    minutes: 13,
    claps: 78,
    url: "/writing/what-was-already-here",
    era: "phase-2",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/what-was-already-here-f11a1b0381f6",
    companionPlatform: "medium",
    featured: true,
    blurb: "Pi Day. Consciousness as stability, not computation. Traveling waves and what was always already here.",
  },
  {
    id: "ditches-are-deadly",
    title: "Ditches Are Deadly",
    date: "2026-03-14",
    minutes: 14,
    claps: 46,
    url: "/writing/ditches-are-deadly",
    era: "phase-2",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/ditches-are-deadly-50128b614402",
    companionPlatform: "medium",
    blurb: "Straightened arroyos, straightened rivers, straightened minds. The geometry of hydraulic violence.",
  },

  // Phase 1 (Feb 2026)
  {
    id: "surviving-adolescence",
    title: "Surviving Adolescence",
    date: "2026-02-21",
    minutes: 23,
    claps: 61,
    url: "/writing/surviving-adolescence",
    era: "phase-1",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/surviving-adolescence-471f48c01160",
    companionPlatform: "medium",
    blurb: "The VLA, Dario, the Context Tensor, and the five failure modes that make machine autonomy safe.",
  },
  {
    id: "bunkers-to-seeds",
    title: "From Building Bunkers to Planting Seeds",
    date: "2026-02-18",
    minutes: 14,
    claps: 1,
    url: "/writing/bunkers-to-seeds",
    era: "phase-1",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/from-building-bunkers-to-planting-seeds-b8db66f24387",
    companionPlatform: "medium",
    blurb: "Ostrom, acequias, and why encoding what survives matters more than defending what exists.",
  },
  {
    id: "abundance-of-trust",
    title: "The Abundance of Trust",
    date: "2026-02-14",
    minutes: 11,
    claps: 52,
    url: "/writing/abundance-of-trust",
    era: "phase-1",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-abundance-of-trust-cec8561ef393",
    companionPlatform: "medium",
    featured: true,
    blurb: "Trust Stack, predictive processing, and Noma as the specimen where proximity becomes proof.",
  },
  {
    id: "the-straightening",
    title: "The Straightening",
    date: "2026-02-12",
    minutes: 12,
    claps: 87,
    url: "/writing/the-straightening",
    era: "phase-1",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-straightening-3b662dbf389e",
    companionPlatform: "medium",
    featured: true,
    blurb: "The minnow. The cognitive fire triangle. Five phases of a system optimized past its own resilience.",
  },

  // Framework (Nov 2025 – Feb 2026)
  // Note on these records: the canonical surface for each Framework-era
  // Medium piece is now /writing/{slug} (KTP-canonical). The Medium URL
  // becomes the companionUrl. Pieces that also have a Substack mirror keep
  // a separate Substack record below; the grouping logic in
  // writingByEraGrouped collapses same-title rows for display.
  {
    id: "pua-fraud-zero-trust-part-2",
    title: "Pandemic Unemployment Assistance Fraud: Zero Trust (Part 2)",
    date: "2026-02-11",
    minutes: 9,
    claps: 100,
    url: "/writing/pua-fraud-zero-trust-part-2",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/pandemic-unemployment-assistance-fraud-zero-trust-part-2-284821c031bf",
    companionPlatform: "medium",
    featured: true,
    blurb: "The empirical foundation. Twenty-three years of fraud detection applied as KTP's first proof.",
  },
  {
    id: "pedagogy-of-digital-physics",
    title: "The Pedagogy of Digital Physics",
    date: "2026-01-26",
    minutes: 19,
    claps: 363,
    url: "/writing/pedagogy-of-digital-physics",
    era: "framework",
    platform: "internal",
    featured: true,
    companionUrl: "https://medium.com/@chrisperkins505/the-pedagogy-of-digital-physics-20c6cb74782c",
    companionPlatform: "medium",
    blurb: "River, economy, and digital networks as isomorphic systems. Same structure, same physics, same mistakes.",
  },
  {
    id: "physics-of-trust",
    title: "The Physics of Trust",
    date: "2026-01-26",
    minutes: 34,
    claps: 110,
    url: "/writing/physics-of-trust",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-physics-of-trust-3416d9c9efea",
    companionPlatform: "medium",
    featured: true,
    blurb: "AI safety, Lovelace's loom, Wolfram's ruliad, and the physics of constraining digital agents.",
  },
  {
    id: "time-to-good-decision",
    title: "Time to Good Decision",
    date: "2026-01-20",
    minutes: 17,
    claps: 52,
    url: "/writing/time-to-good-decision",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/time-to-good-decision-307710ad7a84",
    companionPlatform: "medium",
    blurb: "Food stamps from 1967 and why the design of fraud detection reveals our design for care.",
  },
  {
    id: "pua-fraud-zero-trust-part-1",
    title: "Pandemic Unemployment Assistance Fraud: Zero Trust (Part 1)",
    date: "2026-01-19",
    minutes: 13,
    claps: 50,
    url: "/writing/pua-fraud-zero-trust-part-1",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/pandemic-unemployment-assistance-fraud-zero-trust-part-1-733cf4e39b06",
    companionPlatform: "medium",
    blurb: "How hundreds of billions in pandemic fraud became possible, and why the system designed itself to fail.",
  },
  {
    id: "pua-fraud",
    title: "Pandemic Unemployment Assistance Fraud",
    date: "2026-01-19",
    minutes: 15,
    claps: 50,
    url: "/writing/pua-fraud",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/pandemic-unemployment-assistance-fraud-10fa84912efc",
    companionPlatform: "medium",
    blurb: "Pandemic unemployment assistance fraud as a mirror of the system's true priorities.",
  },
  {
    id: "constitution-of-digital-physics",
    title: "The Constitution of Digital Physics",
    date: "2025-12-12",
    minutes: 8,
    claps: 0,
    url: "/writing/constitution-of-digital-physics",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-constitution-of-digital-physics-157e7e45c1b5",
    companionPlatform: "medium",
    blurb: "Ten articles. Seven dimensions. The immutable laws governing autonomous agents.",
  },
  {
    id: "the-tether",
    title: "The Tether",
    date: "2025-12-12",
    minutes: 8,
    claps: 62,
    url: "/writing/the-tether",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-tether-2288a6b70973",
    companionPlatform: "medium",
    blurb: "Context Tensor made operational: six sensors, one risk factor, the Trust Leash in action.",
  },
  {
    id: "proof-of-physics",
    title: "Proof of Physics",
    date: "2025-12-11",
    minutes: 8,
    claps: 51,
    url: "/writing/proof-of-physics",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/proof-of-physics-2d673d582a98",
    companionPlatform: "medium",
    blurb: "Why identity must become a verb. Trajectory theft-resistant. Lineage as the only true credential.",
  },
  {
    id: "sailing-by-starlight",
    title: "Sailing by Starlight",
    date: "2025-12-10",
    minutes: 10,
    claps: 48,
    url: "/writing/sailing-by-starlight",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/sailing-by-starlight-d4cedff64ddf",
    companionPlatform: "medium",
    blurb: "From administrator to wayfinder. Trust as mass. Polynesian navigation for the digital age.",
  },
  {
    id: "ghost-in-the-machine",
    title: "The Ghost in the Machine",
    date: "2025-12-03",
    minutes: 9,
    claps: 357,
    url: "/writing/ghost-in-the-machine",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-ghost-in-the-machine-76f4b43f07ff",
    companionPlatform: "medium",
    featured: true,
    blurb: "Data Compass, nociception reflex, six physical fields. KTP's sensory architecture made buildable.",
  },
  {
    id: "missing-law-of-motion",
    title: "The Missing Law of Motion",
    date: "2025-11-27",
    minutes: 9,
    claps: 69,
    url: "/writing/missing-law-of-motion",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-missing-law-of-motion-2044294ff551",
    companionPlatform: "medium",
    featured: true,
    blurb: "A ≤ E stated as physics. Stadium scenario, Asimov trap, Silent Veto enforcement.",
  },
  {
    id: "experience-equation",
    title: "The Experience Equation",
    date: "2025-11-17",
    minutes: 10,
    claps: 83,
    url: "/writing/experience-equation",
    era: "framework",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-experience-equation-74953a64c03f",
    companionPlatform: "medium",
    featured: true,
    blurb: "Universal Experience Equation: E = (A × 0.4) + (R × 0.3) + (Q × 0.3).",
  },

  // Voyage (Dec 2025) — KTP-canonical at /writing/{slug}; Medium URL kept
  // as companionUrl so each chapter shows "also on Medium ↗".
  {
    id: "voyage-summary",
    title: "The Voyage: 12 Days of Digital Physics",
    date: "2025-12-29",
    minutes: 3,
    claps: 7,
    url: "/writing/voyage-summary",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-voyage-12-days-of-digital-physics-724ca6f63b44",
    companionPlatform: "medium",
    blurb: "The summary and index. Read this first — or read it last.",
  },
  {
    id: "fire",
    title: "Fire",
    date: "2025-12-24",
    minutes: 8,
    claps: 1,
    url: "/writing/fire",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/fire-ee78299a0420",
    companionPlatform: "medium",
    voyageChapter: 12,
    blurb: "Chapter 12. What remains burning when the islands are reached.",
  },
  {
    id: "archipelago",
    title: "Archipelago",
    date: "2025-12-22",
    minutes: 9,
    claps: 48,
    url: "/writing/archipelago",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/archipelago-3b22711ada09",
    companionPlatform: "medium",
    voyageChapter: 11,
    blurb: "Chapter 11. Islands appear. The destination is not the journey's end.",
  },
  {
    id: "landfall",
    title: "Landfall",
    date: "2025-12-23",
    minutes: 8,
    claps: 32,
    url: "/writing/landfall",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/landfall-9eea3ddd7f43",
    companionPlatform: "medium",
    voyageChapter: 10,
    blurb: "Chapter 10. Ground beneath feet. The moment the ocean becomes the land.",
  },
  {
    id: "sacred",
    title: "Sacred",
    date: "2025-12-21",
    minutes: 8,
    claps: 47,
    url: "/writing/sacred",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/sacred-426a81c8beb6",
    companionPlatform: "medium",
    voyageChapter: 9,
    blurb: "Chapter 9. What cannot be extracted. The shrine at the crossing.",
  },
  {
    id: "horizon",
    title: "Horizon",
    date: "2025-12-20",
    minutes: 7,
    claps: 48,
    url: "/writing/horizon",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/horizon-7c6cc101050c",
    companionPlatform: "medium",
    voyageChapter: 8,
    blurb: "Chapter 8. The line between sky and sea. The vanishing point made visible.",
  },
  {
    id: "storm",
    title: "Storm",
    date: "2025-12-19",
    minutes: 7,
    claps: 3,
    url: "/writing/storm",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/storm-e6f374c21fc7",
    companionPlatform: "medium",
    voyageChapter: 7,
    blurb: "Chapter 7. Wind, waves, the moment control becomes irrelevant.",
  },
  {
    id: "crew",
    title: "Crew",
    date: "2025-12-18",
    minutes: 10,
    claps: 3,
    url: "/writing/crew",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/crew-f3efdcb804c2",
    companionPlatform: "medium",
    voyageChapter: 6,
    blurb: "Chapter 6. Who you travel with matters more than where you are going.",
  },
  {
    id: "current",
    title: "Current",
    date: "2025-12-17",
    minutes: 8,
    claps: 43,
    url: "/writing/current",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/current-745961b089ae",
    companionPlatform: "medium",
    voyageChapter: 5,
    blurb: "Chapter 5. Invisible forces carrying you. Swimming with, not against.",
  },
  {
    id: "swells",
    title: "Swells",
    date: "2025-12-16",
    minutes: 4,
    claps: 52,
    url: "/writing/swells",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/swells-c07cbbb85f3d",
    companionPlatform: "medium",
    voyageChapter: 4,
    blurb: "Chapter 4. The rhythm of the ocean. Patience is a protocol.",
  },
  {
    id: "stars",
    title: "Stars",
    date: "2025-12-15",
    minutes: 5,
    claps: 41,
    url: "/writing/stars",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/stars-db8b363a11dd",
    companionPlatform: "medium",
    voyageChapter: 3,
    blurb: "Chapter 3. Wayfinding by starlight. Everything you need is already above you.",
  },
  {
    id: "vessel",
    title: "Vessel",
    date: "2025-12-14",
    minutes: 5,
    claps: 52,
    url: "/writing/vessel",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/vessel-32bb759ebda2",
    companionPlatform: "medium",
    voyageChapter: 2,
    blurb: "Chapter 2. The boat itself. What holds you through the deep.",
  },
  {
    id: "departure",
    title: "Departure",
    date: "2025-12-13",
    minutes: 7,
    claps: 49,
    url: "/writing/departure",
    era: "voyage",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/departure-525049f52c23",
    companionPlatform: "medium",
    voyageChapter: 1,
    blurb: "Chapter 1. Leaving the harbor. The moment fear becomes clarity.",
  },

  // Trust & Access (Aug – Sep 2025)
  {
    id: "trust-access-office",
    title: "The Trust & Access Office: From Permissions to Proof",
    date: "2025-09-04",
    minutes: 19,
    claps: 51,
    url: "/writing/trust-access-office",
    era: "trust-access",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-trust-access-office-from-permissions-to-proof-91a94615e956",
    companionPlatform: "medium",
    blurb: "Access as product. Defense as a data problem. Time to Good Decision. Identity lifecycle.",
  },
  {
    id: "straw-students",
    title: "Straw Students: AI-Powered Fraud is Infiltrating Higher Education",
    date: "2025-08-13",
    minutes: 7,
    claps: 57,
    url: "/writing/straw-students",
    era: "trust-access",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/straw-students-ai-powered-fraud-is-infiltrating-higher-education-99deb1bfc66f",
    companionPlatform: "medium",
    blurb: "AI-powered fraud reshaping higher education from the inside.",
  },

  // Enterprise (Apr 2023 – May 2025) — KTP-canonical at /writing/{slug}
  {
    id: "soc-ecosystem",
    title: "The SOC Ecosystem: Lessons from \"Le Grand Gambit\"",
    date: "2025-05-27",
    minutes: 28,
    claps: 51,
    url: "/writing/soc-ecosystem",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-soc-ecosystem-lessons-from-le-grand-gambit-6c0796d933e4",
    companionPlatform: "medium",
    blurb: "Security operations centers as organisms. What thrives, what dies, what endures.",
  },
  {
    id: "days-at-risk",
    title: "Days at Risk: How Complacency Fuels Catastrophic Outcomes",
    date: "2025-01-09",
    minutes: 11,
    claps: 293,
    url: "/writing/days-at-risk",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/days-at-risk-how-complacency-fuels-catastrophic-outcomes-80576b12a450",
    companionPlatform: "medium",
    featured: true,
    blurb: "Days at Risk: Risk = L × I × DaR. Time-aware accumulation; the garden hose as the model.",
  },
  {
    id: "protecting-days-at-risk",
    title: "Protecting Your Organization by Measuring \"Days at Risk\"",
    date: "2025-01-06",
    minutes: 6,
    claps: 1,
    url: "/writing/protecting-days-at-risk",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/protecting-your-organization-by-measuring-days-at-risk-1ac03cacd809",
    companionPlatform: "medium",
    blurb: "How to measure and defend your organization's exposure over time.",
  },
  {
    id: "data-to-decisions",
    title: "From Data to Decisions: A Roadmap for Public Sector Innovation",
    date: "2024-12-19",
    minutes: 23,
    claps: 3,
    url: "/writing/data-to-decisions",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/from-data-to-decisions-a-roadmap-for-public-sector-innovation-2d8f1c7bbd7c",
    companionPlatform: "medium",
    blurb: "From data collection to policy. A roadmap for public sector innovation.",
  },
  {
    id: "web-of-data",
    title: "Web of Data",
    date: "2024-11-04",
    minutes: 69,
    claps: 51,
    url: "/writing/web-of-data",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/web-of-data-4e4272c7031c",
    companionPlatform: "medium",
    blurb: "The ur-text. ODAM, data sovereignty, contextual integrity, surveillance, indigenous rights.",
  },
  {
    id: "isey-parking-garage",
    title: "An ISEy Parking Garage: A metaphor",
    date: "2024-08-30",
    minutes: 10,
    claps: 0,
    url: "/writing/isey-parking-garage",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/an-isey-parking-garage-a-metaphor-6720a9c89e1c",
    companionPlatform: "medium",
    blurb: "On parking, security, and the infrastructure metaphor you did not expect.",
  },
  {
    id: "security-usability-pensions",
    title: "The Intersection of Security and Usability in Public Pensions",
    date: "2024-08-14",
    minutes: 21,
    claps: 51,
    url: "/writing/security-usability-pensions",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-intersection-of-security-and-usability-in-public-pensions-234d5ae3b3e5",
    companionPlatform: "medium",
    blurb: "When the secure path and the usable path diverge. Whose burden is it?",
  },
  {
    id: "corporate-storytelling",
    title: "Corporate Storytelling — a Speakeasy Event in Las Vegas",
    date: "2024-07-07",
    minutes: 16,
    claps: 1,
    url: "/writing/corporate-storytelling",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/corporate-storytelling-a-speakeasy-event-in-las-vegas-b29cab646a6f",
    companionPlatform: "medium",
    blurb: "How story architecture shapes what people trust and what they ignore.",
  },
  {
    id: "brief-history-cybersec-ops",
    title: "A Brief History of Cybersecurity Operations",
    date: "2024-05-20",
    minutes: 2,
    claps: 1,
    url: "/writing/brief-history-cybersec-ops",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/a-brief-history-of-cybersecurity-operations-f2748da39268",
    companionPlatform: "medium",
    blurb: "From moat-and-castle to distributed defense. Four decades compressed.",
  },
  {
    id: "chatgpt-data-analysis",
    title: "The Problem With… ChatGPT Performing Data Analysis",
    date: "2024-05-17",
    minutes: 3,
    claps: 53,
    url: "/writing/chatgpt-data-analysis",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-problem-with-chatgpt-performing-data-analysis-4138161b797b",
    companionPlatform: "medium",
    blurb: "Why large language models fail at the data analysis problem.",
  },
  {
    id: "strategy-to-signal",
    title: "Strategy to Signal: Charting the Data Terrain for State Government Innovation",
    date: "2024-03-19",
    minutes: 15,
    claps: 2,
    url: "/writing/strategy-to-signal",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/strategy-to-signal-charting-the-data-terrain-for-state-government-innovation-b22923723161",
    companionPlatform: "medium",
    blurb: "Charting the terrain from strategic intent to operational signal.",
  },
  {
    id: "shift-fraud-resilience",
    title: "SHIFT: The Future of Fraud Resilience",
    date: "2024-01-09",
    minutes: 15,
    claps: 51,
    url: "/writing/shift-fraud-resilience",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/shift-the-future-of-fraud-resilience-d47d42768afd",
    companionPlatform: "medium",
    blurb: "The convergence of cyber and fraud. The future of organizational defense.",
  },
  {
    id: "cyber-fraud-convergence",
    title: "The Future State: Fortifying Security Through Cyber-Fraud Convergence",
    date: "2023-12-04",
    minutes: 8,
    claps: 5,
    url: "/writing/cyber-fraud-convergence",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-future-state-fortifying-security-through-cyber-fraud-convergence-b241a6b304e8",
    companionPlatform: "medium",
    blurb: "Fortifying security through the convergence of cybersecurity and fraud prevention.",
  },
  {
    id: "identity-layer-anti-fraud",
    title: "Why The Identity Layer Is a Critical Battleground For Anti-Fraud",
    date: "2023-12-03",
    minutes: 6,
    claps: 1,
    url: "/writing/identity-layer-anti-fraud",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/why-the-identity-layer-is-a-critical-battleground-for-anti-fraud-1818480644ac",
    companionPlatform: "medium",
    blurb: "The identity layer as the critical battleground for anti-fraud infrastructure.",
  },
  {
    id: "digital-battleground",
    title: "The Digital Battleground: Uniting Cybersecurity and Fraud Prevention in State Agencies",
    date: "2023-12-03",
    minutes: 8,
    claps: 38,
    url: "/writing/digital-battleground",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-digital-battleground-uniting-cybersecurity-and-fraud-prevention-in-state-agencies-8ea46ba8eb5f",
    companionPlatform: "medium",
    blurb: "Uniting cybersecurity and fraud prevention in state agencies.",
  },
  {
    id: "invisible-cartographer",
    title: "The Invisible Cartographer",
    date: "2023-11-10",
    minutes: 14,
    claps: 30,
    url: "/writing/invisible-cartographer",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-invisible-cartographer-f5763d4b659a",
    companionPlatform: "medium",
    blurb: "The earliest published piece. ODAM as narrative; signals, semantics, logic.",
  },
  {
    id: "fraud-splunk-platform",
    title: "Fighting Fraud in the Public Sector with Splunk Data Analytics Platform",
    date: "2023-11-07",
    minutes: 15,
    claps: 67,
    url: "/writing/fraud-splunk-platform",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/fighting-fraud-in-the-public-sector-with-splunk-data-analytics-platform-03cab0fabb29",
    companionPlatform: "medium",
    blurb: "Fighting fraud in the public sector with a data analytics platform.",
  },
  {
    id: "mapping-cyber-terrain",
    title: "Mapping the Cyber Terrain: The Intersection of Cartography and Cybersecurity",
    date: "2023-08-02",
    minutes: 13,
    claps: 105,
    url: "/writing/mapping-cyber-terrain",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/mapping-the-cyber-terrain-the-intersection-of-cartography-and-cybersecurity-69ab57843be1",
    companionPlatform: "medium",
    featured: true,
    blurb: "Second-highest enterprise engagement. Cartography as the model for cyber terrain.",
  },
  {
    id: "odam-revolution",
    title: "ODAM, a Revolution! Reinventing the Cybersecurity Workforce in New Mexico",
    date: "2023-07-28",
    minutes: 12,
    claps: 7,
    url: "/writing/odam-revolution",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/odam-a-revolution-reinventing-the-cybersecurity-workforce-in-new-mexico-7a9717a06366",
    companionPlatform: "medium",
    blurb: "Reinventing the cybersecurity workforce in New Mexico.",
  },
  {
    id: "zero-trust-airport",
    title: "Unraveling Zero Trust: An Airport Security Analogy + ODAM",
    date: "2023-06-30",
    minutes: 6,
    claps: 60,
    url: "/writing/zero-trust-airport",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/unraveling-zero-trust-an-airport-security-analogy-odam-d013b67ae8eb",
    companionPlatform: "medium",
    blurb: "Zero Trust explained with an airport security analogy, plus ODAM.",
  },
  {
    id: "discovering-data-worth",
    title: "Discovering the True Worth of Data: Unlocking Value with ODAM",
    date: "2023-04-14",
    minutes: 8,
    claps: 1,
    url: "/writing/discovering-data-worth",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/discovering-the-true-worth-of-data-unlocking-value-with-odam-fbb007b7b8d8",
    companionPlatform: "medium",
    blurb: "Unlocking value by understanding what data actually costs to produce.",
  },
  {
    id: "odam-digital-resilience",
    title: "ODAM: a Digital Resilience Strategy",
    date: "2023-04-05",
    minutes: 3,
    claps: 11,
    url: "/writing/odam-digital-resilience",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/odam-a-digital-resilience-strategy-fc34f2fbc286",
    companionPlatform: "medium",
    blurb: "ODAM as a digital resilience strategy for operational use.",
  },
  {
    id: "data-in-sled",
    title: "The Future State: Data in SLED",
    date: "2023-04-04",
    minutes: 11,
    claps: 51,
    url: "/writing/data-in-sled",
    era: "enterprise",
    platform: "internal",
    companionUrl: "https://medium.com/@chrisperkins505/the-future-state-data-in-sled-6e632ea8d168",
    companionPlatform: "medium",
    blurb: "The earliest published piece in the corpus. Data governance in state and local government.",
  },
 ] satisfies Writing[]).map((entry): Writing => {
  const metadata = contentByUrl(entry.url);
  const destination = readingDestination(entry.url);
  if (metadata && !metadata.ready && destination !== entry.url) {
    const platform = destination.includes("substack.com") ? "substack" : destination.includes("linkedin.com") ? "linkedin" : "medium";
    return { ...entry, url: destination, platform, readiness: "external-original", companionUrl: entry.companionUrl === destination ? undefined : entry.companionUrl, companionPlatform: entry.companionUrl === destination ? undefined : entry.companionPlatform };
  }
  return { ...entry, readiness: entry.url ? "ready" : "pending" };
});

/** Filter the corpus by era, preserving input order. */
export function writingByEra(era: WritingEra): Writing[] {
  return WRITING.filter((w) => w.era === era);
}

/** One platform's appearance of a writing piece. Many pieces appear on
 *  multiple platforms — `writingByEraGrouped` collapses those into a
 *  single GroupedWriting record with a `platforms` array. */
export type WritingPlatformLink = {
  platform: "medium" | "substack" | "linkedin" | "internal";
  url: string;
  claps: number;
  /** The original article id of this platform's entry. */
  id: string;
};

/** A writing piece, deduplicated across platforms. The same article on
 *  Medium and Substack collapses into a single record with two entries
 *  in `platforms`. The /writing page renders one row per group. */
export type WritingGrouped = {
  /** Stable key (first contributing entry's id). */
  key: string;
  title: string;
  /** Latest date among all platform versions. Used for sort. */
  date: string;
  minutes: number;
  era: WritingEra;
  blurb: string;
  featured: boolean;
  /** Sorted: clappable-with-URL first, by claps desc; empty-URL last. */
  platforms: WritingPlatformLink[];
  /** True if more than one platform carries this piece. */
  multiPlatform: boolean;
  /** Voyage chapter (preserved when present so VoyageList grouping
   *  continues to recognize chapters). */
  voyageChapter?: number;
  /** Substack-day index (same reason). */
  substackDay?: number;
};

/** Comparator for platform links inside a group: rows with a real URL
 *  appear first, ordered by engagement (claps desc); rows with an empty
 *  URL move to the end. Within ties, fall back to a stable platform
 *  order so renders are deterministic. */
const PLATFORM_TIE_BREAK: Record<
  "medium" | "substack" | "linkedin" | "internal",
  number
> = {
  // Internal pieces sort first within a group so the on-site reading
  // path is the primary affordance even when companions exist.
  internal: -1,
  medium: 0,
  substack: 1,
  linkedin: 2,
};

function comparePlatformLinks(
  a: WritingPlatformLink,
  b: WritingPlatformLink
): number {
  const aHas = a.url.length > 0 ? 0 : 1;
  const bHas = b.url.length > 0 ? 0 : 1;
  if (aHas !== bHas) return aHas - bHas;
  if (a.claps !== b.claps) return b.claps - a.claps;
  return PLATFORM_TIE_BREAK[a.platform] - PLATFORM_TIE_BREAK[b.platform];
}

/** Normalize a title for grouping. Same article on different platforms
 *  occasionally drifts in punctuation; trimming + casefolding handles
 *  the cases we have today without false-merging titles that simply
 *  share words. */
function groupKey(title: string): string {
  return title.trim().toLowerCase();
}

/** Filter the corpus by era, group same-title pieces across platforms,
 *  and return chronologically (newest first). The /writing page renders
 *  one row per grouped record. */
export function writingByEraGrouped(era: WritingEra): WritingGrouped[] {
  const inEra = WRITING.filter((w) => w.era === era);
  const byKey = new Map<string, WritingGrouped>();

  for (const w of inEra) {
    const platform = w.platform ?? "medium";
    const key = groupKey(w.title);
    const link: WritingPlatformLink = {
      platform,
      url: w.url,
      claps: w.claps,
      id: w.id,
    };
    const existing = byKey.get(key);
    if (existing) {
      const duplicate = existing.platforms.find((existingLink) => link.url && existingLink.url === link.url && existingLink.platform === link.platform);
      if (duplicate) duplicate.claps = Math.max(duplicate.claps, link.claps);
      else existing.platforms.push(link);
      if (w.date > existing.date) existing.date = w.date;
      if (w.featured) existing.featured = true;
      if (w.blurb && w.blurb.length > existing.blurb.length) {
        existing.blurb = w.blurb;
      }
      if (w.minutes > existing.minutes) existing.minutes = w.minutes;
      if (typeof w.voyageChapter === "number" && existing.voyageChapter === undefined) {
        existing.voyageChapter = w.voyageChapter;
      }
      if (typeof w.substackDay === "number" && existing.substackDay === undefined) {
        existing.substackDay = w.substackDay;
      }
    } else {
      byKey.set(key, {
        key: w.id,
        title: w.title,
        date: w.date,
        minutes: w.minutes,
        era: w.era,
        blurb: w.blurb,
        featured: w.featured ?? false,
        platforms: [link],
        multiPlatform: false,
        voyageChapter: w.voyageChapter,
        substackDay: w.substackDay,
      });
    }
  }

  const grouped = Array.from(byKey.values());
  for (const g of grouped) {
    g.platforms.sort(comparePlatformLinks);
    g.multiPlatform = g.platforms.length > 1;
  }

  return grouped.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/** Voyage chapters only, sorted ascending by chapter number 1..12. */
export function voyageChapters(): Writing[] {
  // Sort by publication date, not by chapter number. The Voyage was
  // published Dec 13–24 2025 in chapter-number order with one exception:
  // Landfall (chapter 10) shipped Dec 23, after Archipelago (chapter 11)
  // which shipped Dec 22. Date order keeps the list honest to what the
  // reader actually saw arrive in their feed.
  return writingByEra("voyage")
    .filter((w) => typeof w.voyageChapter === "number")
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** The Substack "Twelve Days of Digital Physics" series, sorted ascending
 *  by day number 1..12. Companion series to the Voyage — same dates,
 *  different framings. */
export function substackDays(): Writing[] {
  return writingByEra("voyage")
    .filter((w) => typeof w.substackDay === "number")
    .sort((a, b) => (a.substackDay ?? 0) - (b.substackDay ?? 0));
}

/** The summary / key piece for the Voyage era — renders as the entry
 *  marker at the top of that section. */
export function voyageSummary(): Writing | undefined {
  return WRITING.find((w) => w.id === "voyage-summary");
}

export const TOTAL_ESSAYS = WRITING.length;
export const TOTAL_MINUTES = WRITING.reduce((s, w) => s + w.minutes, 0);
export const TOTAL_CLAPS = WRITING.reduce((s, w) => s + w.claps, 0);

const PUBLICATION_DATES = WRITING.map((entry) => entry.date).sort();
export const WRITING_DATE_RANGE = { first: PUBLICATION_DATES[0], last: PUBLICATION_DATES[PUBLICATION_DATES.length - 1] };
